import React, { useContext, useMemo } from 'react'
import '../EarlyWarnings/styles.scss'
import { ReactComponent as IconSatis } from '../../../../../assets/icons/u_alert-check.svg';
import { ReactComponent as IconExcl } from '../../../../../assets/icons/u_alert-exclamacion.svg';
import { ReactComponent as IconMult } from '../../../../../assets/icons/u_alert-multyple.svg';
import { ReactComponent as IconNoAsg } from '../../../../../assets/icons/u_alert-not-assigned.svg';
import HeaderExtended from '../HeaderExtended';
import usePortfolioName from '../../../../../hooks/usePortfolioName';
import { TagFilterContext } from '../../../../../context/TagFilterContext';
import { useTranslation } from 'react-i18next';
import "chartjs-plugin-datalabels";
import { buildAlertGCMInCancelltions } from './buildAlertGCMInCancelltions';
import { buildAlertGCMWarnings } from './buildAlertGCMWarnings';
import { buildAlertGCMInIssues } from './buildAlertGCMInIssues';
import { EarlyWarningsData } from '../GeneralInformation/typesExtendedReport';
import { useEarlyWarningsData } from './useEarlyWarningsData';
import RecentMeasurementsCard from './components/RecentMeasurementsCard';
import PendingSignatureCard from './components/PendingSignatureCard';
import PeriodExpiredCard from './components/PeriodExpiredCard';
import EnvironmentalSocialDistribution from './components/EnvironmentalSocialDistribution';
import PartialCancellations from './components/PartialCancellations';
import PendingFirstDisbursement from './components/PendingFirstDisbursement';
import GcmAlerts from './components/GcmAlerts';
import ReusableIcon from '../shared/ReusableIcon';


type EarlyWarningsProps = {
    data: EarlyWarningsData | null | undefined;
    isLoan?: boolean;
    isTcp?: boolean;
    isIgr?: boolean;
    creationDate?: any;
    regions?: any;
    countries?: string;
    myPortfolio?: string;
    filtersApplied?: any;
}

const COLORS = {
    satisfactory: '#50AC32',
    partiallySatisfactory: '#C7EAA3',
    unsatisfactory: '#E92C2C',
    partiallyUnsatisfactory: '#FF8C26',
    notAsigned: '#D0DEF8'
}

const titleLon = 'LON';
const titleTcp = 'TCP';
const titleIgr = 'IGR';

const options = {
    maintainAspectRatio: false,
    responsive: true,
    legend: {
        display: false,
    },
    plugins: {
        datalabels: false
    },
    scales: {
        yAxes: [
            {
                ticks: {
                    beginAtZero: true,
                    fontSize: 12,
                },
                gridLines: {
                    drawBorder: false,
                },
            },
        ],
        xAxes: [
            {
                gridLines: {
                    display: false,
                    drawBorder: true,
                },
            },
        ],
    },
};

const EarlyWarnings: React.FC<EarlyWarningsProps> = ({ data, isLoan, isTcp, isIgr, creationDate, regions, countries, myPortfolio, filtersApplied }) => {
    const { t } = useTranslation();
    const portfolioName = usePortfolioName();
    const { labels } = useContext(TagFilterContext);
    const { data: dataAlertGCMInCancelltions, options: optionsAlertGCMInCancelltions } =
        useMemo(() => buildAlertGCMInCancelltions(data?.gcmAlerts), [data?.gcmAlerts]);

    const { data: dataAlertGCMWarnings, options: optionsAlertGCMWarnings } =
        useMemo(() => buildAlertGCMWarnings(data?.gcmAlerts), [data?.gcmAlerts]);

    const { data: dataAlertGCMInIssues, options: optionsAlertGCMInIssues } =
        useMemo(() => buildAlertGCMInIssues(data?.gcmAlerts), [data?.gcmAlerts]);

    const mp = useEarlyWarningsData(data);
    if (!data || !mp) return null;

    const efaLegend = [
        { label: `${t('one_pager_report.satisfactory')}`, value: `${mp.EsgSafeGuardPerformanceRating.satisfactory}`, color: COLORS.satisfactory },
        { label: `${t('extended_report.partially_satisfactory')}`, value: `${mp.EsgSafeGuardPerformanceRating.partially_Satisfactory}`, color: COLORS.partiallySatisfactory },
        { label: `${t('extended_report.unsatisfactory')}`, value: `${mp.EsgSafeGuardPerformanceRating.unsatisfactory}`, color: COLORS.unsatisfactory },
    ];
    const efaLegendNext = [
        { label: `${t('extended_report.partially_unsatisfactory')}`, value: `${mp.EsgSafeGuardPerformanceRating.partially_Unsatisfactory}`, color: COLORS.partiallyUnsatisfactory },
        { label: `${t('extended_report.not_asigned')}`, value: `${mp.EsgSafeGuardPerformanceRating.not_Assigned}`, color: COLORS.notAsigned },
    ];

    const dataBar = {
        labels: [" ", " ", " ", " ", " "],
        datasets: [
            {
                label: "Valores",
                data: [
                    mp.EsgSafeGuardPerformanceRating.satisfactory || null,
                    mp.EsgSafeGuardPerformanceRating.partially_Satisfactory || null,
                    mp.EsgSafeGuardPerformanceRating.unsatisfactory || null,
                    mp.EsgSafeGuardPerformanceRating.partially_Unsatisfactory || null,
                    mp.EsgSafeGuardPerformanceRating.not_Assigned || null
                ],
                backgroundColor: [
                    COLORS.satisfactory,
                    COLORS.partiallySatisfactory,
                    COLORS.unsatisfactory,
                    COLORS.partiallyUnsatisfactory,
                    COLORS.notAsigned,
                ],
                borderWidth: 0,
                barThickness: 20,
            },
        ],
    };

    return (
        <>
            <div className='content-early c-main'>
                <HeaderExtended
                    portfolioName={portfolioName}
                    filtersApplied={filtersApplied ?? labels}
                    creationDate={creationDate}
                    regions={regions}
                    countries={countries}
                    myPortfolio={myPortfolio}
                />
                <section className="alerts-early">
                    <header className="header-content">
                        <p>{t('extended_report.early_warning_system')}</p>
                        <div className='flex-avatars'>
                            {(isLoan) && (<ReusableIcon title={titleLon} color="#BE86D8" />)}
                            {(isIgr) && (<ReusableIcon title={titleIgr} color="#F1AF43" />)}
                            <div className='new-3mm-early'><p>≥ 3MM</p></div>
                        </div>
                    </header>

                    <div className="alerts-grid-early">
                        <section className="area area--tl">
                            {/* RecentMeasurementsCard.tsx */}
                            <RecentMeasurementsCard
                                isLoan={isLoan}
                                isIgr={isIgr}
                                IconSatis={IconSatis}
                                IconExcl={IconExcl}
                                IconMult={IconMult}
                                mp={mp}
                            />

                            {/* PendingSignatureCard.tsx */}
                            <PendingSignatureCard
                                isLoan={isLoan}
                                isIgr={isIgr}
                                IconSatis={IconSatis}
                                IconExcl={IconExcl}
                                IconMult={IconMult}
                                mp={mp}
                            />

                            {/* PeriodExpiredCard */}
                            <PeriodExpiredCard
                                isLoan={isLoan}
                                isIgr={isIgr}
                                IconSatis={IconSatis}
                                IconExcl={IconExcl}
                                IconMult={IconMult}
                                mp={mp}
                            />
                        </section>

                        <section className="area area--tm">
                            <EnvironmentalSocialDistribution
                                isLoan={isLoan}
                                isIgr={isIgr}
                                efaLegend={efaLegend}
                                efaLegendNext={efaLegendNext}
                                dataBar={dataBar}
                                options={options}
                            />
                        </section>

                        <section className="area area--tr">
                            {/* PartialCancellations.tsx */}
                            <PartialCancellations
                                isLoan={isLoan}
                                isIgr={isIgr}
                                IconSatis={IconSatis}
                                IconExcl={IconExcl}
                                IconMult={IconMult}
                                mp={mp}
                            />

                            {/* PendingFirstDisbursement.tsx */}
                            <PendingFirstDisbursement
                                isLoan={isLoan}
                                isIgr={isIgr}
                                IconSatis={IconSatis}
                                IconExcl={IconExcl}
                                IconMult={IconMult}
                                mp={mp}
                            />
                        </section>

                        <section className="area area--bottom-early">
                            <header className="header-content">
                                <p>{t('extended_report.GCM_alerts')}</p>
                                <div className='flex-avatars'>
                                    {(isTcp) && (<ReusableIcon title={titleTcp} color="#66B6B6" />)}
                                    {(isIgr) && (<ReusableIcon title={titleIgr} color="#F1AF43" />)}
                                    <div className='new-3mm-early'>
                                        <p>{`< 3MM`}</p>
                                    </div>
                                </div>
                            </header>

                            {/* GcmAlerts.tsx */}
                            <GcmAlerts
                                isTcp={isTcp}
                                IconMult={IconMult}
                                mp={mp}
                                dataAlertGCMInCancelltions={dataAlertGCMInCancelltions}
                                optionsAlertGCMInCancelltions={optionsAlertGCMInCancelltions}
                                IconExcl={IconExcl}
                                dataAlertGCMWarnings={dataAlertGCMWarnings}
                                optionsAlertGCMWarnings={optionsAlertGCMWarnings}
                                IconNoAsg={IconNoAsg}
                                dataAlertGCMInIssues={dataAlertGCMInIssues}
                                optionsAlertGCMInIssues={optionsAlertGCMInIssues}
                            />
                        </section>
                    </div>
                </section>
            </div>
        </>
    )
}

export default EarlyWarnings